'use client';

import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Users } from 'lucide-react';
import { subjectIcons, subjectLabels, formatGradeLevels } from '@/lib/course-display';
import { formatPrice } from '@/lib/utils';
import { MidTermBadge } from '@/components/enrollment/mid-term-badge';
import type { PublicCourseWithMidTerm } from '@/server/services/course.service';

export type CourseDetailContentProps = {
  course: PublicCourseWithMidTerm;
};

export function CourseDetailContent({ course }: CourseDetailContentProps) {
  const t = useTranslations('public.courseCard');
  const SubjectIcon = subjectIcons[course.subject] ?? BookOpen;
  const gradeRange = formatGradeLevels(course.gradeLevels);

  return (
    <div>
      {/* Hero banner (16:9) */}
      <div className="relative mb-6 aspect-[16/9] w-full overflow-hidden rounded-xl">
        {course.thumbnailUrl ? (
          <img
            src={course.thumbnailUrl}
            alt={t('thumbnailAlt', { title: course.title })}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            role="img"
            aria-label={t('noThumbnailAlt', { title: course.title })}
            className="flex h-full w-full items-center justify-center bg-gradient-to-br from-eifo-primary/10 to-eifo-primary/5"
          >
            <SubjectIcon className="h-16 w-16 text-eifo-primary/40" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Subject icon + badge + grade badge */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SubjectIcon className="h-6 w-6 text-eifo-primary" aria-hidden="true" />
        <Badge variant="secondary">
          {subjectLabels[course.subject] ?? course.subject}
        </Badge>
        {gradeRange && (
          <Badge variant="outline" className="text-eifo-primary border-eifo-primary">
            {gradeRange}
          </Badge>
        )}
      </div>

      {/* Course title */}
      <h1 className="text-3xl font-bold text-eifo-primary mb-2">{course.title}</h1>

      {/* Mid-term badge (if available) */}
      {course.midTermAvailability && (
        <div className="mb-3">
          <MidTermBadge midTermAvailability={course.midTermAvailability} />
        </div>
      )}

      {/* Quick stats row */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <Users className="h-4 w-4" aria-hidden="true" />
          {t('maxStudentsLong', { count: course.maxClassSize })}
        </span>
        <span className="font-medium">
          {t('perTerm', { price: formatPrice(course.pricePerTermCents) })}
        </span>
      </div>
    </div>
  );
}
